'use client';

import { useMemo, useCallback } from 'react';
import { Button } from '@heroui/react';
import { useScheduler, AdditionalContactSelection } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { ContactRoleType, Service, Bundle } from '@/util/types/serverTypes';
import { FaArrowRight, FaRotateLeft } from 'react-icons/fa6';
import { motion } from 'framer-motion';
import ContactRoleSelector from './ContactRoleSelector';
import { useStartOver } from '../hooks/useStartOver';

type AdditionalContactsStepProps = {
  services: Service[];
  bundles: Bundle[];
  contactRoles: ContactRoleType[];
};

// Helper to find service in services array
const findService = (serviceId: string, services: Service[]): Service | undefined => {
  return services.find((s) => s._id === serviceId);
};

// Helper to identify Client's Agent role for ordering (ATT-193: move to top)
const isClientsAgentRole = (role: ContactRoleType): boolean => {
  const name = role.name?.toLowerCase() || '';
  return name.includes("client's agent") || name.includes('clients agent');
};

export default function AdditionalContactsStep({
  services,
  bundles,
  contactRoles,
}: AdditionalContactsStepProps) {
  const {
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,
    selectedRole,
    additionalContacts,
    setSelectedRole,
    companyStyles,
  } = useScheduler();
  const { nextStep } = useStepNavigation();

  // Get all selected services (primary + addons + bundle services)
  const selectedServices = useMemo(() => {
    const serviceList: Service[] = [];
    const serviceIdsSet = new Set<string>();

    // Add primary service
    if (selectedPrimaryService) {
      serviceIdsSet.add(selectedPrimaryService);
      const primary = findService(selectedPrimaryService, services);
      if (primary) serviceList.push(primary);
    }

    // Add addon services
    selectedAddons.forEach((addonId) => {
      if (!serviceIdsSet.has(addonId)) {
        serviceIdsSet.add(addonId);
        const addon = findService(addonId, services);
        if (addon) serviceList.push(addon);
      }
    });

    // Add services from selected bundle
    if (selectedBundle) {
      const bundle = bundles.find((b) => b._id === selectedBundle);
      bundle?.items?.forEach((item) => {
        if (item._serviceId && !serviceIdsSet.has(item._serviceId)) {
          serviceIdsSet.add(item._serviceId);
          const service = findService(item._serviceId, services);
          if (service) serviceList.push(service);
        }
      });
    }

    return serviceList;
  }, [selectedPrimaryService, selectedAddons, selectedBundle, bundles, services]);

  // Calculate required role IDs from all selected services
  const requiredRoleIds = useMemo(() => {
    const roleIdsSet = new Set<string>();

    // Extract role IDs from all selected services
    selectedServices.forEach((service) => {
      if (service._contactRoleId && Array.isArray(service._contactRoleId)) {
        service._contactRoleId.forEach((roleId) => {
          roleIdsSet.add(roleId);
        });
      }
    });

    return Array.from(roleIdsSet);
  }, [selectedServices]);

  // Get all required roles (all roles now support multiple contacts)
  // Roles with contacts will still be shown, but displaying the contact info instead of the form
  // Sort roles so the current user's role (selectedRole) appears at the bottom
  const remainingRoles = useMemo(() => {
    // If no role IDs are required, return empty
    if (requiredRoleIds.length === 0) {
      return [];
    }

    // Show all required roles (all roles now support multiple contacts)
    const filteredRoles = contactRoles.filter((role) => {
      return requiredRoleIds.includes(role._id);
    });

    // Sort: Client's Agent first (ATT-193), then selectedRole last
    return filteredRoles.sort((a, b) => {
      const aIsClientsAgent = isClientsAgentRole(a);
      const bIsClientsAgent = isClientsAgentRole(b);
      if (aIsClientsAgent && !bIsClientsAgent) return -1;
      if (!aIsClientsAgent && bIsClientsAgent) return 1;
      if (selectedRole) {
        if (a._id === selectedRole._id) return 1;
        if (b._id === selectedRole._id) return -1;
      }
      return 0;
    });
  }, [contactRoles, requiredRoleIds, selectedRole]);

  // Check if all required roles have contacts added
  const allRequiredContactsCollected = useMemo(() => {
    if (requiredRoleIds.length === 0) {
      return true;
    }

    const rolesWithContacts = new Set(additionalContacts.map((contact) => contact._roleId));

    // Check if all required roles (except primary) have at least one contact
    return requiredRoleIds.every((roleId) => {
      // Primary role is handled separately, so skip it
      if (selectedRole && roleId === selectedRole._id) {
        return true;
      }
      // Check if this role has at least one contact added
      return rolesWithContacts.has(roleId);
    });
  }, [requiredRoleIds, additionalContacts, selectedRole]);

  const handleStartOver = useStartOver({
    navigateToStep: 1,
    useCallback: true, // Performance optimization
    onBeforeReset: () => {
      // Clear selectedRole so services step starts at step 0 (Filter Form) instead of step 1
      setSelectedRole(null);
    },
  });

  // Get all contacts for a role (all roles now support multiple contacts)
  const getContactsForRole = (roleId: string): AdditionalContactSelection[] =>
    additionalContacts.filter((contact) => contact._roleId === roleId);

  const startOverButton = (
    <div className='flex w-full justify-center border-t pt-4'>
      <div className='w-full max-w-64'>
        <Button
          variant='flat'
          color='warning'
          startContent={<FaRotateLeft />}
          onPress={handleStartOver}
          className='w-full'
        >
          Start Over
        </Button>
      </div>
    </div>
  );

  return (
    <div className='mx-auto max-w-4xl space-y-6'>
      <div className='text-center'>
        <h2 className='mb-2 text-3xl font-bold'>Add Additional Contacts You May Know</h2>
        <p className='text-gray-600'>
          {allRequiredContactsCollected
            ? 'All required contacts have been added'
            : 'Add contacts for the remaining roles for this service'}
        </p>
      </div>

      {allRequiredContactsCollected ? (
        <div className='rounded-lg border border-gray-300 bg-white p-8 text-center'>
          <p className='text-gray-600'>All required contact information has been collected.</p>
          <div className='mt-6 flex flex-col items-center gap-4'>
            <Button
              size='lg'
              endContent={<FaArrowRight />}
              onPress={nextStep}
              className={`min-w-40 ${companyStyles.secondaryButtonClassName}`}
              style={companyStyles.secondaryButtonStyles}
            >
              Continue to Summary
            </Button>
            {startOverButton}
          </div>
        </div>
      ) : (
        <>
          <div className='space-y-4'>
            {remainingRoles.map((role, index) => (
              <motion.div
                key={role._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ContactRoleSelector
                  role={role}
                  contactsForRole={getContactsForRole(role._id)}
                  isPrimaryRole={selectedRole?._id === role._id}
                  selectedRole={selectedRole}
                />
              </motion.div>
            ))}
          </div>

          <div className='flex flex-col items-center gap-4 pt-6'>
            <div className='flex w-full justify-center gap-4'>
              <Button size='lg' variant='flat' onPress={nextStep} className='min-w-40'>
                Skip for Now
              </Button>
              <Button
                size='lg'
                endContent={<FaArrowRight />}
                onPress={nextStep}
                className={`min-w-40 font-semibold ${companyStyles.secondaryButtonClassName}`}
                style={companyStyles.secondaryButtonStyles}
              >
                Continue
              </Button>
            </div>
            {startOverButton}
          </div>
        </>
      )}
    </div>
  );
}
